import type { ReactNode } from 'react'
import type { GuideShot } from './guideTypes'
import { pickLang } from './guideTypes'

interface Props {
  shot: GuideShot
  language: string
}

/**
 * Mini HTML replicas of real SmartAccount screens (gradient header + card + table/form).
 * Used when no screenshot image is available.
 */
export default function GuideHtmlMock({ shot, language }: Props) {
  const isAr = language === 'ar'
  const motif = shot.motif ?? 'list'
  const title = pickLang(shot.caption, language)
  const highlight = shot.highlight ? pickLang(shot.highlight, language) : null

  return (
    <div
      className="guide-app-mock relative overflow-hidden rounded-2xl border border-slate-200 shadow-2xl"
      style={{ aspectRatio: '16 / 10', background: '#f3f4f6' }}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {motif === 'list' && <ListScreen isAr={isAr} title={title} />}
      {motif === 'form' && <FormScreen isAr={isAr} title={title} />}
      {motif === 'search' && <SearchScreen isAr={isAr} title={title} />}
      {motif === 'dashboard' && <DashboardScreen isAr={isAr} title={title} />}
      {motif === 'payment' && <PaymentScreen isAr={isAr} title={title} />}
      {motif === 'print' && <PrintScreen isAr={isAr} title={title} />}
      {motif === 'approve' && <ApproveScreen isAr={isAr} title={title} />}
      {motif === 'settings' && <SettingsScreen isAr={isAr} title={title} />}

      {highlight && (
        <div className="pointer-events-none absolute inset-x-3 bottom-3 z-20">
          <div className="guide-pulse rounded-xl bg-amber-400 px-3 py-1.5 text-center text-[11px] font-bold text-slate-900 shadow-lg ring-2 ring-amber-200">
            {highlight}
          </div>
        </div>
      )}
    </div>
  )
}

/* ——— Shared pieces matching real app ——— */

function PurpleHeader({
  isAr,
  title,
  actions,
}: {
  isAr: boolean
  title: string
  actions?: ReactNode
}) {
  return (
    <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-2.5 text-white shadow">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="truncate text-sm font-bold">{title}</h2>
        <div className="flex shrink-0 items-center gap-1">{actions}</div>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="rounded-md bg-white/20 px-2 py-1 text-[9px]">{isAr ? 'بحث' : 'Search'}</div>
        <div className="h-7 flex-1 rounded-md bg-white/90 px-2 text-[10px] leading-7 text-slate-400">
          {isAr ? 'ابحث...' : 'Search...'}
        </div>
      </div>
    </div>
  )
}

function BlueHeader({ isAr, title }: { isAr: boolean; title: string }) {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2.5 text-white shadow">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-white/20 px-2 py-1 text-[9px]">{isAr ? 'رجوع' : 'Back'}</div>
          <h2 className="truncate text-sm font-bold">{title}</h2>
        </div>
        <div className="flex gap-1">
          <div className="rounded-md bg-white/20 px-2 py-1 text-[9px]">{isAr ? 'طباعة' : 'Print'}</div>
          <div className="rounded-md bg-white px-2 py-1 text-[9px] font-bold text-blue-700">{isAr ? 'حفظ' : 'Save'}</div>
        </div>
      </div>
    </div>
  )
}

function HeaderBtn({ children, solid }: { children: ReactNode; solid?: boolean }) {
  return (
    <span
      className={`rounded-md px-2 py-1 text-[9px] font-semibold ${
        solid ? 'bg-white text-violet-700 shadow' : 'bg-white/20 text-white'
      }`}
    >
      {children}
    </span>
  )
}

function Field({ label }: { label: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[9px] font-medium text-slate-600">{label}</div>
      <div className="h-7 rounded-lg border border-slate-300 bg-white px-2 shadow-sm">
        <div className="mt-2 h-1 w-2/3 rounded bg-slate-200" />
      </div>
    </div>
  )
}

/* ——— Screen motifs ——— */

function ListScreen({ isAr, title }: { isAr: boolean; title: string }) {
  const cols = isAr ? ['م', 'الاسم', 'الفرع', 'الحالة'] : ['#', 'Name', 'Branch', 'Status']
  const rows = isAr
    ? [
        ['1', 'المخزن الرئيسي', 'الفرع المركزي', 'نشط'],
        ['2', 'مخزن الفرع', 'فرع الشمال', 'نشط'],
        ['3', 'مخزن الطوارئ', 'الفرع المركزي', 'موقوف'],
        ['4', 'مستودع التبريد', 'فرع الجنوب', 'نشط'],
      ]
    : [
        ['1', 'Main store', 'Central', 'Active'],
        ['2', 'Branch store', 'North', 'Active'],
        ['3', 'Emergency', 'Central', 'Off'],
        ['4', 'Cold storage', 'South', 'Active'],
      ]

  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <PurpleHeader
        isAr={isAr}
        title={title}
        actions={
          <>
            <HeaderBtn>{isAr ? 'تصدير' : 'Export'}</HeaderBtn>
            <HeaderBtn solid>{isAr ? '+ إضافة' : '+ Add'}</HeaderBtn>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow">
        <div className="border-b border-slate-200 bg-slate-50 px-2 py-1.5">
          <div className="flex gap-1.5">
            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[8px] text-emerald-700">
              {isAr ? 'عرض' : 'View'}
            </span>
            <span className="rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[8px] text-amber-700">
              {isAr ? 'تعديل' : 'Edit'}
            </span>
            <span className="rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[8px] text-red-700">
              {isAr ? 'حذف' : 'Delete'}
            </span>
          </div>
        </div>
        <table className="w-full text-[9px]">
          <thead>
            <tr className="bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700">
              {cols.map((c) => (
                <th key={c} className="px-2 py-1.5 font-semibold">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className={`border-t border-slate-100 ${i === 1 ? 'bg-violet-50 ring-1 ring-inset ring-violet-400' : i % 2 ? 'bg-slate-50' : 'bg-white'}`}
              >
                {r.map((cell, j) => (
                  <td key={j} className={`px-2 py-1.5 ${j === 1 ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-slate-200 px-2 py-1 text-[8px] text-slate-500">
          <span>{isAr ? 'عرض 1 إلى 4 من 4' : 'Showing 1 to 4 of 4'}</span>
          <div className="flex gap-1">
            <span className="rounded border border-slate-200 px-1.5">1</span>
            <span className="rounded border border-violet-300 bg-violet-100 px-1.5 text-violet-700">2</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function FormScreen({ isAr, title }: { isAr: boolean; title: string }) {
  const labels = isAr
    ? ['المخزن *', 'التاريخ *', 'المورد / العميل', 'الفرع', 'البيان', 'المرجع']
    : ['Store *', 'Date *', 'Party', 'Branch', 'Notes', 'Reference']

  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <BlueHeader isAr={isAr} title={title} />
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white p-2.5 shadow">
        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {labels.map((l) => (
            <Field key={l} label={l} />
          ))}
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                {(isAr ? ['الصنف', 'الوحدة', 'الكمية', 'السعر', 'الإجمالي'] : ['Item', 'Unit', 'Qty', 'Price', 'Total']).map(
                  (h) => (
                    <th key={h} className="px-1.5 py-1 font-semibold">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {[1, 2].map((i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-1.5 py-1">
                    <div className="h-6 rounded border border-slate-200 bg-slate-50" />
                  </td>
                  <td className="px-1.5 py-1">
                    <div className="h-6 w-12 rounded border border-slate-200 bg-slate-50" />
                  </td>
                  <td className="px-1.5 py-1 text-center text-slate-700">{i === 1 ? '10' : '2'}</td>
                  <td className="px-1.5 py-1 text-center text-slate-700">{i === 1 ? '5.00' : '12.00'}</td>
                  <td className="px-1.5 py-1 text-center font-semibold text-slate-800">{i === 1 ? '50.00' : '24.00'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="rounded-md border border-dashed border-violet-300 bg-violet-50 px-2 py-1 text-[9px] text-violet-700">
            {isAr ? '+ إضافة صنف' : '+ Add line'}
          </div>
          <div className="text-[10px] font-bold text-slate-800">
            {isAr ? 'الإجمالي: 74.00' : 'Total: 74.00'}
          </div>
        </div>
      </div>
    </div>
  )
}

function SearchScreen({ isAr, title }: { isAr: boolean; title: string }) {
  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <PurpleHeader
        isAr={isAr}
        title={title}
        actions={<HeaderBtn solid>{isAr ? 'عرض' : 'View'}</HeaderBtn>}
      />
      <div className="flex gap-1.5 px-0.5">
        {(isAr ? ['من تاريخ', 'إلى تاريخ', 'الفرع', 'الحالة'] : ['From', 'To', 'Branch', 'Status']).map((f) => (
          <div key={f} className="flex-1">
            <div className="mb-0.5 text-[8px] text-slate-500">{f}</div>
            <div className="h-7 rounded-lg border border-slate-300 bg-white shadow-sm" />
          </div>
        ))}
      </div>
      <div className="min-h-0 flex-1 rounded-lg border border-slate-200 bg-white p-2 shadow">
        {[1, 2, 3].map((i) => (
          <div key={i} className="mb-1.5 flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5">
            <div className="h-7 w-7 rounded-md bg-violet-100" />
            <div className="flex-1">
              <div className="h-1.5 w-2/3 rounded bg-slate-300" />
              <div className="mt-1 h-1 w-1/3 rounded bg-slate-200" />
            </div>
            <div className="rounded-full bg-emerald-100 px-2 py-0.5 text-[8px] text-emerald-700">
              {isAr ? 'مطابق' : 'Match'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DashboardScreen({ isAr, title }: { isAr: boolean; title: string }) {
  const cards = isAr
    ? [
        { t: 'المبيعات', v: '128,400', c: 'from-violet-500 to-purple-600' },
        { t: 'المشتريات', v: '74,200', c: 'from-blue-500 to-indigo-600' },
        { t: 'المستحقات', v: '42,900', c: 'from-emerald-500 to-teal-600' },
      ]
    : [
        { t: 'Sales', v: '128,400', c: 'from-violet-500 to-purple-600' },
        { t: 'Purchases', v: '74,200', c: 'from-blue-500 to-indigo-600' },
        { t: 'Receivables', v: '42,900', c: 'from-emerald-500 to-teal-600' },
      ]

  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <div className="rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 px-3 py-2 text-white shadow">
        <h2 className="text-sm font-bold">{title || (isAr ? 'لوحة التحكم' : 'Dashboard')}</h2>
        <p className="text-[9px] text-white/80">{isAr ? 'ملخص اليوم' : "Today's summary"}</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {cards.map((c) => (
          <div key={c.t} className={`rounded-lg bg-gradient-to-br ${c.c} p-2 text-white shadow`}>
            <div className="text-[9px] opacity-90">{c.t}</div>
            <div className="text-sm font-black">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="min-h-0 flex-1 rounded-lg border border-slate-200 bg-white p-2 shadow">
        <div className="mb-1 text-[9px] font-semibold text-slate-600">{isAr ? 'منحنى الأداء' : 'Performance'}</div>
        <div className="flex h-[70%] items-end gap-1 px-1">
          {[35, 55, 40, 70, 50, 85, 60].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-violet-600 to-purple-400" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  )
}

function PaymentScreen({ isAr, title }: { isAr: boolean; title: string }) {
  const methods = isAr ? ['نقدي', 'شبكة', 'آجل'] : ['Cash', 'Card', 'Credit']
  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <BlueHeader isAr={isAr} title={title} />
      <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-slate-200 bg-white p-3 shadow">
        <div className="text-[10px] text-slate-500">{isAr ? 'المبلغ المستحق' : 'Amount due'}</div>
        <div className="mb-3 text-2xl font-black text-slate-800">38.00</div>
        <div className="mb-3 flex gap-2">
          {methods.map((m, i) => (
            <div
              key={m}
              className={`rounded-lg px-3 py-2 text-[10px] font-bold shadow-sm ${
                i === 0 ? 'bg-violet-600 text-white' : 'border border-slate-200 bg-slate-50 text-slate-700'
              }`}
            >
              {m}
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-2 text-[11px] font-bold text-white shadow">
          {isAr ? 'تأكيد الدفع' : 'Confirm payment'}
        </div>
      </div>
    </div>
  )
}

function PrintScreen({ isAr, title }: { isAr: boolean; title: string }) {
  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <PurpleHeader
        isAr={isAr}
        title={title}
        actions={
          <>
            <HeaderBtn>Excel</HeaderBtn>
            <HeaderBtn solid>{isAr ? 'طباعة' : 'Print'}</HeaderBtn>
          </>
        }
      />
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 shadow">
        <div className="mx-auto h-full max-w-[70%] rounded border border-dashed border-slate-300 bg-slate-50 p-2">
          <div className="mb-2 text-center text-[10px] font-bold text-slate-700">{title}</div>
          <div className="space-y-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-1.5 rounded bg-slate-200" style={{ width: `${90 - i * 8}%` }} />
            ))}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 border-t border-slate-200 pt-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 rounded bg-white border border-slate-200" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ApproveScreen({ isAr, title }: { isAr: boolean; title: string }) {
  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <BlueHeader isAr={isAr} title={title} />
      <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-slate-200 bg-white shadow">
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-xl text-emerald-600">
          ✓
        </div>
        <div className="mb-1 text-sm font-bold text-slate-800">{isAr ? 'تمت العملية بنجاح' : 'Completed successfully'}</div>
        <div className="mb-3 text-[10px] text-slate-500">{title}</div>
        <div className="flex gap-2">
          <div className="rounded-lg border border-slate-200 px-3 py-1.5 text-[10px] text-slate-600">
            {isAr ? 'العودة' : 'Back'}
          </div>
          <div className="rounded-lg bg-violet-600 px-3 py-1.5 text-[10px] font-bold text-white">
            {isAr ? 'متابعة' : 'Continue'}
          </div>
        </div>
      </div>
    </div>
  )
}

function SettingsScreen({ isAr, title }: { isAr: boolean; title: string }) {
  const rows = isAr
    ? ['بيانات الشركة', 'طرق الدفع', 'إعدادات الفاتورة', 'الألوان والمظهر', 'المستخدمون والصلاحيات']
    : ['Company profile', 'Payment methods', 'Invoice settings', 'Colors & theme', 'Users & roles']

  return (
    <div className="flex h-full flex-col gap-2 p-2">
      <PurpleHeader isAr={isAr} title={title} actions={<HeaderBtn solid>{isAr ? 'حفظ' : 'Save'}</HeaderBtn>} />
      <div className="min-h-0 flex-1 space-y-1.5 overflow-hidden rounded-lg border border-slate-200 bg-white p-2 shadow">
        {rows.map((r, i) => (
          <div key={r} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-violet-600 text-[9px] font-bold text-white">
              {i + 1}
            </div>
            <div className="flex-1 text-[10px] font-semibold text-slate-700">{r}</div>
            <div className={`h-4 w-7 rounded-full p-0.5 ${i % 2 === 0 ? 'bg-violet-600' : 'bg-slate-300'}`}>
              <div className={`h-3 w-3 rounded-full bg-white ${i % 2 === 0 ? 'ms-auto' : ''}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
